import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import { isNil } from "ramda";
import React, { FC, memo, Suspense } from "react";
import { RatelistRowName } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RatelistRowName";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { useRateDataContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";
import { RateBuilderRowContainer } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowContent";
import { RateConditionOverview } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/RateConditionOverview";
import { RateUnitOverview } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/RateUnitOverview";
import { RateOverview } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/RateOverview";
import { ComponentOverview } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/ComponentOverview";
import {
  ComponentContentItem,
  ConditionContentItem,
  RateContentItem,
  UnitContentItem,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/styled";

export const RateBuilderRowOverview: FC<{ rowId: string }> = memo(({ rowId }) => {
  const { data } = useRateDataContext<RateBuilderTreeFormFields>();

  const row = data.Rate[rowId];
  // This condition prevent render row before data from tree are propagated into form
  if (isNil(row)) {
    return null;
  }

  return (
    <Stack sx={{ minWidth: "320px", pr: 2 }}>
      <RatelistRowName rowId={rowId} />
      <Grid alignItems="center" container spacing={1} sx={{ padding: "1px" }}>
        <Grid size="grow">
          <Suspense fallback={<RateBuilderRowOverviewFallback />}>
            <RateBuilderRowContainer container spacing={1} sx={{ position: "relative" }}>
              <ConditionContentItem className="RateBuilderRowContentItem-full" size={{ xs: 12, lg: "grow" }}>
                <RateConditionOverview condition={row.condition} />
                <Divider
                  orientation="vertical"
                  sx={(theme) => ({
                    height: "1rem",
                    [theme.breakpoints.down("lg")]: {
                      display: "none",
                    },
                  })}
                />
              </ConditionContentItem>
              <Grid
                container
                size={{ xs: 12, lg: "auto" }}
                spacing={1}
                sx={(theme) => ({
                  minWidth: "40vw",
                  alignItems: "center",
                  [theme.breakpoints.down("md")]: {
                    minWidth: "100%",
                    width: "100%",
                  },
                })}
              >
                <UnitContentItem size={{ xs: "grow", lg: 4 }}>
                  <RateUnitOverview rate={row.rate} />
                </UnitContentItem>
                <RateContentItem size={{ xs: "grow", lg: 4 }}>
                  <RateOverview rate={row.rate} rowId={rowId} />
                  <Divider orientation="vertical" sx={{ height: "1rem" }} />
                </RateContentItem>
                <ComponentContentItem size={{ xs: "auto", lg: 4 }}>
                  <ComponentOverview rate={row.rate} />
                </ComponentContentItem>
              </Grid>
            </RateBuilderRowContainer>
          </Suspense>
        </Grid>
      </Grid>
    </Stack>
  );
});
RateBuilderRowOverview.displayName = "RateBuilderRowOverview";

const FallbackSkeleton: FC = () => (
  <Skeleton sx={{ height: "1.2rem", backgroundColor: "grey.300", width: "100%" }} variant="rounded" />
);

const RateBuilderRowOverviewFallback: FC = () => (
  <RateBuilderRowContainer container spacing={1} sx={{ position: "relative" }}>
    <ConditionContentItem className="RateBuilderRowContentItem-full" size={{ xs: 12, lg: "grow" }}>
      <FallbackSkeleton />
      <Divider
        orientation="vertical"
        sx={(theme) => ({
          height: "1rem",
          [theme.breakpoints.down("lg")]: {
            display: "none",
          },
        })}
      />
    </ConditionContentItem>
    <Grid
      container
      size={{ xs: 12, lg: "auto" }}
      spacing={1}
      sx={(theme) => ({
        minWidth: "40vw",
        [theme.breakpoints.down("md")]: {
          minWidth: "100%",
          width: "100%",
        },
      })}
    >
      <UnitContentItem size={{ xs: "grow", lg: 4 }}>
        <FallbackSkeleton />
      </UnitContentItem>
      <RateContentItem size={{ xs: "grow", lg: 4 }}>
        <FallbackSkeleton />
        <Divider orientation="vertical" sx={{ height: "1rem" }} />
      </RateContentItem>
      <ComponentContentItem size={{ xs: "auto", lg: 4 }}>
        <FallbackSkeleton />
      </ComponentContentItem>
    </Grid>
  </RateBuilderRowContainer>
);
